import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { BOOK, EXCERPTS } from '@/data/singleHanded';
import {
  BookOpen,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Calendar,
  Clock,
  Headphones,
  ArrowRight,
  Mail,
} from 'lucide-react';

function formatTime(sec) {
  if (!isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function ExcerptCard({ excerpt, isActive, onPlay }) {
  const mediaRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (!isActive && playing && mediaRef.current) {
      mediaRef.current.pause();
      setPlaying(false);
    }
  }, [isActive, playing]);

  const toggle = () => {
    const el = mediaRef.current;
    if (!el) return;
    if (el.paused) {
      onPlay(excerpt.id);
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const seek = (e) => {
    const el = mediaRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    el.currentTime = Math.max(0, Math.min(duration, ratio * duration));
  };

  const toggleMute = () => {
    const el = mediaRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <article className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl border border-slate-200 transition-all duration-300 flex flex-col">
      {/* Thumbnail with big play overlay */}
      <button
        onClick={toggle}
        className="relative aspect-video overflow-hidden bg-slate-900"
        aria-label={`Play ${excerpt.title}`}
      >
        <img
          src={excerpt.thumbnail}
          alt={excerpt.title}
          loading="lazy"
          className="w-full h-full object-cover opacity-70 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-16 w-16 rounded-full bg-amber-500 group-hover:bg-amber-400 flex items-center justify-center shadow-xl transition-all group-hover:scale-110">
            {playing ? (
              <Pause className="h-7 w-7 text-[#0b2545]" fill="currentColor" />
            ) : (
              <Play className="h-7 w-7 text-[#0b2545] ml-1" fill="currentColor" />
            )}
          </div>
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center gap-3 text-white text-xs">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-black/50 backdrop-blur-sm">
            <Headphones className="h-3 w-3" /> Narrated
          </span>
          {excerpt.date && (
            <span className="inline-flex items-center gap-1 opacity-90">
              <Calendar className="h-3 w-3" /> {excerpt.date}
            </span>
          )}
        </div>
      </button>

      {/* Card body */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-serif text-xl font-bold text-[#0b2545] leading-snug mb-2">
          {excerpt.title}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">
          {excerpt.description}
        </p>

        {/* Compact player controls */}
        <div className="mt-auto rounded-md bg-slate-50 border border-slate-200 p-3">
          <video
            ref={mediaRef}
            src={excerpt.audio}
            preload="metadata"
            className="hidden"
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime || 0)}
            onEnded={() => {
              setPlaying(false);
              setProgress(0);
            }}
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
          />
          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              className="h-9 w-9 flex-shrink-0 rounded-full bg-[#0b2545] hover:bg-[#123a6b] text-amber-400 flex items-center justify-center transition-colors"
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing ? (
                <Pause className="h-4 w-4" fill="currentColor" />
              ) : (
                <Play className="h-4 w-4 ml-0.5" fill="currentColor" />
              )}
            </button>
            <div className="flex-1">
              <div
                onClick={seek}
                className="h-1.5 w-full bg-slate-200 rounded-full cursor-pointer overflow-hidden"
              >
                <div
                  className="h-full bg-amber-500 transition-[width] duration-100"
                  style={{
                    width: duration ? `${(progress / duration) * 100}%` : '0%',
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500 tabular-nums">
                <span>{formatTime(progress)}</span>
                <span>{excerpt.duration || (duration ? formatTime(duration) : '—')}</span>
              </div>
            </div>
            <button
              onClick={toggleMute}
              className="h-8 w-8 flex-shrink-0 rounded-md hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors"
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function SingleHanded() {
  const [activeId, setActiveId] = useState(null);

  return (
    <div>
      <SEO
        title="Single Handed — Narrated Excerpts"
        description="Listen to selected narrated excerpts from Single Handed — the book by Lieutenant Joe Kress on one man’s fight against the drug crisis."
        path="/single-handed"
        type="book"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0b2545] via-[#123a6b] to-[#0b2545] text-white">
        <div className="absolute inset-0 opacity-20 mix-blend-luminosity"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #f59e0b 0%, transparent 40%), radial-gradient(circle at 80% 70%, #f59e0b 0%, transparent 40%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28 grid md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr] gap-10 md:gap-16 items-center">
          {/* Book cover */}
          <div className="mx-auto md:mx-0">
            <div className="relative">
              <div className="absolute -inset-4 bg-amber-500/20 blur-2xl rounded-full" />
              <img
                src={BOOK.cover}
                alt={`${BOOK.title} book cover`}
                className="relative w-full max-w-[280px] rounded-md shadow-2xl border border-white/10"
              />
            </div>
          </div>

          {/* Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-300/30 text-amber-300 text-xs font-semibold uppercase tracking-widest mb-5">
              <BookOpen className="h-3.5 w-3.5" /> The Book
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
              SINGLE<br />
              <span className="text-amber-400">HANDED</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed max-w-xl">
              {BOOK.subtitle}
            </p>
            <p className="mt-4 text-sm text-slate-300 max-w-xl leading-relaxed">
              Listen below to a growing library of narrated excerpts — straight from the pages of Lt. Joe Kress’s book on his fight against the drug crisis.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-semibold"
              >
                <Link to="/single-handed/buy">
                  <BookOpen className="mr-2 h-4 w-4" /> Buy the Ebook — $4.99
                </Link>
              </Button>
              <Button
                onClick={() => {
                  document.getElementById('excerpts')?.scrollIntoView({ behavior: 'smooth' });
                }}
                size="lg"
                variant="outline"
                className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0b2545]"
              >
                <Headphones className="mr-2 h-4 w-4" /> Listen to Excerpts
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0b2545]">
                <Link to="/bio">About the Author</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Book intro */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-3">About Single Handed</div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545] leading-tight mb-5">
            One man’s fight against the drug crisis — in his own words.
          </h2>
          <div className="h-1 w-16 bg-amber-400 mb-6" />
          <p className="text-slate-700 leading-[1.9] text-[16px]">
            {BOOK.intro}
          </p>
        </div>
      </section>

      {/* Excerpts library */}
      <section id="excerpts" className="py-20 max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">Narrated Excerpts</div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545]">Listen to the Book</h2>
            <div className="mt-3 h-1 w-16 bg-amber-400" />
          </div>
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-500" />
            {EXCERPTS.length} excerpt{EXCERPTS.length !== 1 ? 's' : ''} available
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {EXCERPTS.map((e) => (
            <ExcerptCard
              key={e.id}
              excerpt={e}
              isActive={activeId === e.id}
              onPlay={setActiveId}
            />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-[200px_1fr] gap-8 items-center bg-gradient-to-br from-[#0b2545] to-[#123a6b] rounded-lg p-8 md:p-12 text-white">
            <img
              src={BOOK.cover}
              alt={`${BOOK.title}`}
              className="w-full max-w-[180px] mx-auto rounded-md shadow-2xl"
              loading="lazy"
            />
            <div>
              <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.3em] font-semibold mb-3">
                <BookOpen className="h-4 w-4" /> Get in touch
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-3">Want to learn more about Single Handed?</h2>
              <p className="text-slate-200 leading-relaxed mb-6 max-w-xl">
                Reach out to Kress &amp; Associates for information about the book, to schedule a school presentation, or to support the mission.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild className="bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-semibold">
                  <Link to="/single-handed/buy">
                    <BookOpen className="mr-2 h-4 w-4" /> Buy the Ebook — $4.99
                  </Link>
                </Button>
                <Button asChild variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0b2545]">
                  <Link to="/contact">
                    <Mail className="mr-2 h-4 w-4" /> Contact Us
                  </Link>
                </Button>
                <Button asChild variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0b2545]">
                  <Link to="/donate">
                    Support the Mission <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
